import React from "react";
import { createPortal } from "react-dom";
import styles from "./PromoModals.module.scss";

interface PromoModalsProps {
  showWeekdayPromoModal: boolean;
  showWeekendPromoModal1: boolean;
  showWeekendPromoModal2: boolean;
  onCloseWeekday: () => void;
  onCloseWeekend1: () => void;
  onCloseWeekend2: () => void;
}

interface PromoConfig {
  accentColor: string;
  accentDark: string;
  label: string;
  title: string;
  description: string;
  timeLabel: string;
  timeNote: string;
  price: string;
  priceUnit: string;
}

const PROMO_CONFIG: Record<string, PromoConfig> = {
  weekday: {
    accentColor: "#1EB2A6",
    accentDark: "#158F85",
    label: "PADEL АКЦИЯ",
    title: "Padel промо\n(будни)",
    description:
      "Забронируйте 3 корта Padel на 2 часа подряд и получите специальную цену!",
    timeLabel: "21:00 — 23:00",
    timeNote: "Только в будние дни",
    price: "3 000 ₽",
    priceUnit: "за корт / час",
  },
  weekend1: {
    accentColor: "#1EB2A6",
    accentDark: "#158F85",
    label: "PADEL АКЦИЯ",
    title: "Padel промо\n(выходные)",
    description:
      "Забронируйте 3 корта Padel на 2 часа подряд и получите специальную цену!",
    timeLabel: "10:00 — 18:00",
    timeNote: "Только в выходные дни",
    price: "3 000 ₽",
    priceUnit: "за корт / час",
  },
  weekend2: {
    accentColor: "#2B8956",
    accentDark: "#1E6940",
    label: "HARD АКЦИЯ",
    title: "Hard промо\n(выходные)",
    description:
      "Забронируйте все 4 корта Hard на одно время и получите специальную цену!",
    timeLabel: "14:00 — 19:00",
    timeNote: "5 часов в выходные дни",
    price: "3 000 ₽",
    priceUnit: "за корт / час",
  },
};

interface SinglePromoModalProps {
  isOpen: boolean;
  onClose: () => void;
  configKey: string;
}

const SinglePromoModal: React.FC<SinglePromoModalProps> = ({ isOpen, onClose, configKey }) => {
  const config = PROMO_CONFIG[configKey];

  React.useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen || !config) return null;

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
        {/* Drag handle */}
        <div className={styles.handle} />

        {/* Colored header */}
        <div
          className={styles.header}
          style={{ background: `linear-gradient(135deg, ${config.accentColor} 0%, ${config.accentDark} 100%)` }}
        >
          <div className={styles.headerContent}>
            <span className={styles.label}>{config.label}</span>
            <h2 className={styles.title}>
              {config.title.split("\n").map((line, i) => (
                <React.Fragment key={i}>{line}{i === 0 && <br />}</React.Fragment>
              ))}
            </h2>
          </div>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Закрыть">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 1L13 13M13 1L1 13" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className={styles.body}>
          {/* Description row */}
          <div className={styles.row}>
            <span className={styles.checkDot} style={{ background: config.accentColor }}>✓</span>
            <p className={styles.descText}>{config.description}</p>
          </div>

          {/* Time block */}
          <div className={styles.timeBlock}>
            <span className={styles.timeEmoji}>🕐</span>
            <div>
              <span className={styles.timeBig} style={{ color: config.accentColor }}>
                {config.timeLabel}
              </span>
              <span className={styles.timeSmall}>{config.timeNote}</span>
            </div>
          </div>

          <div className={styles.divider} />

          {/* Price */}
          <div
            className={styles.priceCard}
            style={{ borderColor: config.accentColor + "50" }}
          >
            <span className={styles.priceCardLabel}>Фиксированная цена</span>
            <span className={styles.priceValue} style={{ color: config.accentColor }}>
              {config.price}
            </span>
            <span className={styles.priceUnit}>{config.priceUnit}</span>
          </div>

          {/* CTA */}
          <button
            className={styles.ctaBtn}
            style={{ background: `linear-gradient(135deg, ${config.accentColor} 0%, ${config.accentDark} 100%)` }}
            onClick={onClose}
          >
            Понятно
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export const PromoModals: React.FC<PromoModalsProps> = ({
  showWeekdayPromoModal,
  showWeekendPromoModal1,
  showWeekendPromoModal2,
  onCloseWeekday,
  onCloseWeekend1,
  onCloseWeekend2,
}) => {
  return (
    <>
      <SinglePromoModal isOpen={showWeekdayPromoModal} onClose={onCloseWeekday} configKey="weekday" />
      <SinglePromoModal isOpen={showWeekendPromoModal1} onClose={onCloseWeekend1} configKey="weekend1" />
      <SinglePromoModal isOpen={showWeekendPromoModal2} onClose={onCloseWeekend2} configKey="weekend2" />
    </>
  );
};
